import { useMemo } from "react";
import { object } from "prop-types";
import { observer } from "mobx-react";
import { roundFloat } from "@repo/utils";
import { CELL_WIDTHS } from "@utils";
import { useEstimateStructure, CollapseValueCell, ValueCell } from "@tools";
import { useBudgetTrackingStore } from "@views/Projects/BudgetTracking/providers/BudgetTrackingStore";
import { makeStyles } from "@material-ui/styles"

export const CostCell = observer(({ workType }) => {
  const structure = useEstimateStructure();
  const classes = useStyle();
  const { cost } = useBudgetTrackingStore();

  const { showFixedDecimalPrices } = structure;
  const {
    currencyObj,
    roundPrice,
    useMinMax,
    usedDecimals,
    usedRounding,
  } = structure.settings;
  const {
    parsedPrice,
    displayBTPrice,
    btPriceDiff,
    btPriceDiffPercent
  } = workType;

  const colorText = useMemo(() => {
    if (parsedPrice.avg !== 0) {
      return btPriceDiff.avg > 0 ? classes.rowAccepted : classes.rowDanger;
    }
    return null;
  }, [btPriceDiff.avg]);
  
  const value = useMemo(
    () =>
      roundFloat(
        useMinMax ? parsedPrice.max || 0 : parsedPrice.avg || 0,
        usedRounding,
        false,
        usedDecimals
      ),
    [parsedPrice, useMinMax, usedRounding, usedDecimals]
  );
  
  const btPriceDiffs = useMemo(
    () =>
      roundFloat(
        (useMinMax ? btPriceDiff.max || 0 : btPriceDiff.avg || 0),
        usedRounding,
        false,
        usedDecimals
      ),
    [useMinMax, roundPrice, useMinMax]
  );

  const btPriceDiffPercents = useMemo(
    () =>
      roundFloat(
        (useMinMax ? btPriceDiffPercent.max || 0 : btPriceDiffPercent.avg || 0),
        usedRounding,
        false,
        usedDecimals
      ),
    [useMinMax, roundPrice, useMinMax]
  );


  return (
    <CollapseValueCell
      childrenWidth={CELL_WIDTHS.VALUE.XXSMALL}
      mainCellWidth={CELL_WIDTHS.VALUE.LARGE}
      isOpened={cost}
      cell={
        <ValueCell
          size="large"
          displayContent
          value={btPriceDiffPercents}
          symbolEnd="%"
          showDecimals={!roundPrice}
          fixedDecimalScale={showFixedDecimalPrices}
          max={10000000}
          className={classes.cell}
          textPresetClass="preset-summaryText"
        />
      }
    >
      <ValueCell
        size="xsmall"
        displayContent
        useInputBackground
        usePriceFormat
        value={value}
        symbolStart={currencyObj.symbolStart}
        symbolEnd={currencyObj.symbolEnd}
        showDecimals={!roundPrice}
        fixedDecimalScale={showFixedDecimalPrices}
        max={10000000}
        textPresetClass="preset-price"
      />
      <ValueCell
        size="xsmall"
        displayContent
        value={displayBTPrice || 0}
        symbolStart={displayBTPrice ? currencyObj.symbolStart : undefined}
        symbolEnd={displayBTPrice ? currencyObj.symbolEnd : undefined}
        showDecimals={!roundPrice}
        fixedDecimalScale={showFixedDecimalPrices}
        max={10000000}
        textPresetClass={colorText}
      />
      <ValueCell
        size="xsmall"
        displayContent
        value={btPriceDiffs}
        symbolEnd={currencyObj.symbolEnd}
        showDecimals={!roundPrice}
        fixedDecimalScale={showFixedDecimalPrices}
        textPresetClass={colorText}
        max={10000000}
        showValueSign
      />
      <ValueCell
        size="xsmall"
        displayContent
        value={btPriceDiffPercents}
        symbolEnd="%"
        showDecimals={!roundPrice}
        fixedDecimalScale={showFixedDecimalPrices}
        textPresetClass={colorText}
        max={10000000}
        showValueSign
      />
    </CollapseValueCell>
  );
});

CostCell.propTypes = {
  workType: object.isRequired,
};

const useStyle = makeStyles(() => ({
  cell: {
    zIndex: 2,
  },
  rowDanger: {
    color: "#41BD56",
  },
  rowAccepted: {
    color: "#DE0909",
  },
}));
